import { useCallback } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import {
  AddUpdateCuboidInput,
  useAddUpdateCuboidMutation,
  DeleteCuboidInput,
  useDeleteCuboidMutation,
} from '@/__generated__/graphql';
import { LabellingCuboid } from '@/types';
import { CuboidBase, CuboidDataHandlers } from '@/modules/cuboids/types';
import { useCuboidState } from './useCuboidState';
import * as state from '@/components/AssetLabelling/state';
import * as globalState from '@/state';
import { generateMongoObjectId } from '@/utils/databaseHelpers';

export const useCuboidDataHandlers = (): CuboidDataHandlers<LabellingCuboid> => {
  const cuboidState = useCuboidState();

  const [cuboids, setCuboids] = useAtom(cuboidState.cuboids);

  const structure = useAtomValue(state.structure);
  const selectedZone = useAtomValue(globalState.selectedZone);
  const defaultCuboidClassId = useAtomValue(state.defaultCuboidClassId);

  const [addUpdateCuboidMutation, { loading: updateLoading, error: updateError }] =
    useAddUpdateCuboidMutation();

  const [deleteCuboidMutation, { loading: deleteLoading, error: deleteError }] =
    useDeleteCuboidMutation();

  const canCreateCuboid = useCallback(
    () => !!(structure && selectedZone && defaultCuboidClassId),
    [structure, selectedZone, defaultCuboidClassId]
  );

  const createCuboid = useCallback(
    ({ position, rotation, scale }: CuboidBase): LabellingCuboid | undefined => {
      if (!structure) return;
      if (!selectedZone) return;
      if (!defaultCuboidClassId) return;

      const id = generateMongoObjectId();

      const newCuboid: LabellingCuboid = {
        id,
        structureId: structure.id,
        zoneId: selectedZone.id,
        cuboidClassId: defaultCuboidClassId,
        position,
        rotation,
        scale,
        isNew: true,
      };
      return newCuboid;
    },
    [structure, selectedZone, defaultCuboidClassId]
  );

  const addUpdateCuboid = useCallback(
    (cuboid: LabellingCuboid) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { isNew, ...savedCuboid } = cuboid;
      const input: AddUpdateCuboidInput = savedCuboid;

      addUpdateCuboidMutation({
        variables: {
          input,
        },
      });

      setCuboids((current) => {
        const newCuboids = current.asMutableStateMap();
        newCuboids?.set(savedCuboid.id, savedCuboid);
        return newCuboids.asStateMap();
      });
    },
    [setCuboids, addUpdateCuboidMutation]
  );

  const deleteCuboid = useCallback(
    (cuboid: LabellingCuboid) => {
      if (!cuboid.id) return;

      if (!cuboid?.isNew) {
        const input: DeleteCuboidInput = {
          id: cuboid.id,
        };

        deleteCuboidMutation({
          variables: {
            input,
          },
        });
      }

      setCuboids((current) => {
        const newCuboids = current.asMutableStateMap();
        newCuboids.delete(cuboid.id);
        return newCuboids?.asStateMap();
      });
    },
    [setCuboids, deleteCuboidMutation]
  );

  return {
    cuboids,
    canCreateCuboid,
    createCuboid,
    addUpdateCuboid,
    deleteCuboid,
    updateLoading,
    updateError,
    deleteLoading,
    deleteError,
  };
};
